import React, { useMemo, useRef, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);
const fmt = (s) => {
  const m = Math.floor((s || 0) / 60);
  const sec = Math.floor((s || 0) % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

// Admin creator for interactive guided-demo steps: loads the MP4, lets the
// admin pause at a moment, drag a rectangle over the video frame, and attach
// hint text. Hotspots are stored as percentages of the frame so they scale.
export function HotspotCreator({ url, hotspots = [], onChange, description = "", onDescriptionChange }) {
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const [armDraw, setArmDraw] = useState(false); // draw mode on/off
  const [drag, setDrag] = useState(null); // {x0,y0,x1,y1} in %
  const [pending, setPending] = useState(null); // finalized box awaiting hint text
  const [text, setText] = useState("");
  const [err, setErr] = useState("");

  const sorted = useMemo(() => [...(hotspots || [])].sort((a, b) => a.t - b.t), [hotspots]);

  const pct = (e) => {
    const r = overlayRef.current.getBoundingClientRect();
    const cx = (e.clientX - r.left) / r.width;
    const cy = (e.clientY - r.top) / r.height;
    return { x: Math.min(Math.max(cx, 0), 1) * 100, y: Math.min(Math.max(cy, 0), 1) * 100 };
  };

  const startDraw = () => {
    videoRef.current?.pause();
    setErr("");
    setPending(null);
    setArmDraw(true);
  };

  const onDown = (e) => {
    if (!armDraw) return;
    e.preventDefault();
    const p = pct(e);
    setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
    e.target.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag) return;
    const p = pct(e);
    setDrag({ ...drag, x1: p.x, y1: p.y });
  };
  const onUp = () => {
    if (!drag) return;
    const x = Math.min(drag.x0, drag.x1);
    const y = Math.min(drag.y0, drag.y1);
    const w = Math.abs(drag.x1 - drag.x0);
    const h = Math.abs(drag.y1 - drag.y0);
    setDrag(null);
    if (w < 2 || h < 2) return; // too small, ignore
    const t = videoRef.current ? videoRef.current.currentTime : 0;
    setPending({ id: uid(), t: Math.round(t * 10) / 10, x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, w: Math.round(w * 10) / 10, h: Math.round(h * 10) / 10, text: "" });
    setText("");
    setArmDraw(false);
  };

  const savePending = () => {
    if (!pending) return;
    if (!text.trim()) { setErr("Add the hint text for this hotspot."); return; }
    onChange([...hotspots, { ...pending, text: text.trim() }]);
    setPending(null);
    setText("");
    setErr("");
  };

  const box = drag
    ? { x: Math.min(drag.x0, drag.x1), y: Math.min(drag.y0, drag.y1), w: Math.abs(drag.x1 - drag.x0), h: Math.abs(drag.y1 - drag.y0) }
    : null;

  return (
    <div className="flex flex-col gap-2" data-testid="hotspot-creator">
      {!url ? (
        <div className="rounded-xl bg-slate-100 p-4 text-center text-slate-500 text-xs">Paste a direct MP4 URL above to load the video, then add hotspots.</div>
      ) : (
        <div className="relative rounded-xl overflow-hidden bg-slate-900 select-none">
          <video ref={videoRef} src={url} controls playsInline className="w-full" data-testid="creator-video" />
          {(armDraw || box || pending) && (
            <div
              ref={overlayRef}
              className={`absolute inset-0 z-10 ${armDraw ? "cursor-crosshair" : ""}`}
              style={{ touchAction: "none" }}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              data-testid="creator-draw-overlay"
            >
              {box && (
                <div className="absolute border-2 border-dashed border-[#FF5E1A] bg-[#FF5E1A]/10 rounded-lg"
                  style={{ left: `${box.x}%`, top: `${box.y}%`, width: `${box.w}%`, height: `${box.h}%` }} />
              )}
              {pending && (
                <div className="absolute border-2 border-[#FF5E1A] cpn-hotspot-ripple rounded-lg"
                  style={{ left: `${pending.x}%`, top: `${pending.y}%`, width: `${pending.w}%`, height: `${pending.h}%` }}
                  data-testid="creator-pending-box" />
              )}
            </div>
          )}
        </div>
      )}

      {url && !pending && (
        <button type="button" onClick={startDraw} data-testid="hotspot-add-btn"
          className="self-start inline-flex items-center gap-1 rounded-full bg-white border border-slate-300 hover:border-[#FF5E1A] px-2.5 py-1 text-[11px] font-semibold">
          <Plus className="w-3 h-3" /> Add hotspot at current time
        </button>
      )}
      {armDraw && <div className="text-[11px] text-[#E04F00] font-medium">Drag a rectangle over the area you want to highlight.</div>}

      {pending && (
        <div className="rounded-xl bg-[#FFF3ED] border border-[#FFD9C7] p-3 flex flex-col gap-2" data-testid="hotspot-form">
          <div className="text-[11px] font-bold text-[#E04F00]">Hotspot at {fmt(pending.t)} — add the hint text</div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Click on 'View / Add Customer' to open the customer list"
            data-testid="hotspot-text-input" className="w-full px-2 py-1.5 rounded-md glass-input text-sm min-h-[50px]" autoFocus />
          {err && <div className="text-[11px] text-red-600">{err}</div>}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setPending(null); setErr(""); }} className="px-3 py-1.5 text-xs rounded-lg bg-white border border-slate-300">Cancel</button>
            <button type="button" onClick={savePending} data-testid="hotspot-save" className="px-3 py-1.5 text-xs rounded-lg bg-[#FF5E1A] text-white font-semibold">Save hotspot</button>
          </div>
        </div>
      )}

      {sorted.length > 0 && (
        <div className="flex flex-col gap-1.5" data-testid="hotspot-list">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Hotspots ({sorted.length})</div>
          {sorted.map((h, i) => (
            <div key={h.id} className="flex items-center gap-2 rounded-lg bg-white border border-slate-200 px-2 py-1.5" data-testid={`hotspot-item-${i}`}>
              <input type="number" min="0" step="0.5" value={h.t} title="Time (seconds)"
                onChange={(e) => onChange(hotspots.map((x) => x.id === h.id ? { ...x, t: Math.max(0, parseFloat(e.target.value) || 0) } : x))}
                className="w-16 px-1.5 py-1 rounded-md glass-input text-xs font-mono" />
              <span className="text-[10px] text-slate-400 font-mono shrink-0">{fmt(h.t)}</span>
              <input type="text" value={h.text} placeholder="Hint text"
                onChange={(e) => onChange(hotspots.map((x) => x.id === h.id ? { ...x, text: e.target.value } : x))}
                data-testid={`hotspot-text-${i}`} className="flex-1 px-2 py-1 rounded-md glass-input text-xs" />
              <button type="button" onClick={() => onChange(hotspots.filter((x) => x.id !== h.id))}
                data-testid={`hotspot-delete-${i}`} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      <textarea value={description} onChange={(e) => onDescriptionChange(e.target.value)}
        placeholder="Write-up for this video (optional) — shown below the video, scrollable"
        data-testid="step-description" className="w-full px-3 py-2 rounded-lg glass-input text-sm min-h-[60px]" />
    </div>
  );
}
